import { createContext, useContext, useEffect, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import { toast } from "react-toastify";
import { ParseErrors } from "../utils/ParseErrors";
import formQueryUrl from "../utils/formQueryUrl";
import { AuthContext } from "./AuthProvider";


export interface ISingleConversation {
    id: number;
    firstName: string;
    surname: string;
    messages: {
        id: number;
        message: string;
        isRead: boolean
        type: "portal" | "dashboard"
    }[]
    unReadMessageCount: number,
    policyReferenceNumber: string,
    lastUpdated: string,
    repliedBy: "p" | "d"
}

interface IConversations {
    totalCount: number;
    data: ISingleConversation[]
}

interface ILoading {
    isFetcchingConversations: boolean;
}

type ValueChatContext = {
    loading: ILoading;
    currentConversation: ISingleConversation | undefined
    setCurrentConversation: (data: ISingleConversation) => void
    conversations: IConversations
    getConversations: ({ query }: { query?: Record<string, any>[] }) => void
    loadMoreConversations: ({ query }: { query?: Record<string, any>[] }) => void
    updateConversationLastMessage: (messages: any) => void
    unReadMessageCount: number
};

export const ChatContext = createContext<ValueChatContext | undefined>(undefined);

const ChatProvider: React.FC<{ children?: React.ReactNode }> = ({
    children,
}) => {
    const [currentConversation, setCurrentConversation] = useState<ISingleConversation | undefined>(undefined)
    const [conversations, setConversations] = useState<IConversations | null>(null)
    const [loading, setLoading] = useState<ILoading>({ isFetcchingConversations: false })
    const [unReadMessageCount, setUnReadMessageCount] = useState<number>(0)
    const { pathName, user } = useContext(AuthContext)

    // ------------------------- [ FIND CONVERSATIONS ] -----------------------------
    const getConversations = async ({ query }: { query?: Record<string, any>[] }) => {
        setConversations({ totalCount: 0, data: [] })
        setLoading((prev) => ({ ...prev, isFetcchingConversations: true }))
        try {
            let queryData = [{ limit: 10 }, ...query]
            const res = await axiosInstance.get(`/chat/chase/conversations${formQueryUrl(queryData, '?')}`)
            setConversations(res.data)
            setLoading((prev) => ({ ...prev, isFetcchingConversations: false }))
        } catch (error) {
            console.log(error)
            toast.error(ParseErrors(error))
            setLoading((prev) => ({ ...prev, isFetcchingConversations: false }))
        }
    }

    // ------------------------- [ FIND CONVERSATIONS ] -----------------------------
    const loadMoreConversations = async ({ query }: { query?: Record<string, any>[] }) => {
        setLoading((prev) => ({ ...prev, isFetcchingConversations: true }))
        try {
            let queryData = [{ limit: 10 }, { page: conversations.data.length }, ...query]
            const res = await axiosInstance.get(`/chat/chase/conversations${formQueryUrl(queryData, '?')}`)
            setConversations((prev) => ({ totalCount: res.data.totalCount, data: [...prev.data, ...res.data.data] }))
            setLoading((prev) => ({ ...prev, isFetcchingConversations: false }))
        } catch (error) {
            console.log(error)
            toast.error(ParseErrors(error))
            setLoading((prev) => ({ ...prev, isFetcchingConversations: false }))
        }
    }


    // -------------------------------- [ UPDATE CONVERSATION LAST MESSAGE ] ----------------
    const updateConversationLastMessage = (message: any) => {
        let newConversations = conversations.data.find((item) => item.id === message.chaseId)
        setConversations((prev) => ({ ...prev, data: [{ ...newConversations, messages: [message], lastUpdated: new Date().toDateString() }, ...prev.data.filter((item) => item.id !== message.chaseId)] }))
    }


    // ------------------------ [ FIND UNREAD MESSAGES ] -----------------------------------

    useEffect(() => {
        const findUnreadMessages = async () => {
            try {
                const res = await axiosInstance.get(pathName === "/my-customers" ? `/chat/unread/messages/all?customerCode=${user?.code}` : `/chat/unread/messages/all`)
                setUnReadMessageCount(res.data)
            } catch (error) {
                console.log(error)
            }
        }

        findUnreadMessages()
    }, [pathName, user?.code])

    return (
        <ChatContext.Provider value={{ loading, conversations, getConversations, currentConversation, setCurrentConversation, updateConversationLastMessage, loadMoreConversations, unReadMessageCount }}>
            {children}
        </ChatContext.Provider>
    )
};

export default ChatProvider;
